/* Shared util: render product-cards for many handles, beating Shopify's
   ~20 all_products[handle] per-render limit by batching ≤20 handles per
   Section Rendering request to the search page (sections/products-by-handle.liquid).

   window.ProductsByHandle.fetchCardsByHandle(handles, context) -> Promise<HTMLElement[]>
   window.ProductsByHandle.sortCards(cards, key)               -> HTMLElement[]
   No lazy-load: callers fetch the full set, then sort, then render. */
(function () {
  const ROOT = window.Shopify?.routes?.root || '/';
  const SEARCH_URL = `${ROOT}search`.replace('//', '/');
  const BATCH = 20;

  function chunk(arr, size) {
    const out = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
  }

  async function fetchBatch(handles, context) {
    const q = encodeURIComponent([context, ...handles].join(','));
    // Contexts render different card shapes: 'swatch' emits color-combination tiles,
    // 'slider' emits the default vertical <product-card>, everything else horizontal.
    const ctx = String(context);
    let selector = '.product-card--horizontal';
    if (ctx.startsWith('swatch')) selector = '.color-combinations__item';
    else if (ctx.startsWith('slider')) selector = 'product-card';
    try {
      const res = await fetch(`${SEARCH_URL}?section_id=products-by-handle&q=${q}`);
      if (!res.ok) return [];
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      return [...doc.querySelectorAll(selector)];
    } catch (e) {
      console.error('products-by-handle fetch failed', e);
      return [];
    }
  }

  /* Dedupes + trims handles, fetches every ≤20 batch in parallel, returns the
     merged card nodes (caller sorts + appends). */
  async function fetchCardsByHandle(handles, context = 'cart') {
    const unique = [...new Set((handles || []).map((h) => String(h).trim()).filter(Boolean))];
    if (!unique.length) return [];
    const results = await Promise.all(chunk(unique, BATCH).map((b) => fetchBatch(b, context)));
    return results.flat();
  }

  /* key: 'bestseller' (data-bestseller desc) | 'price_desc' (data-price desc). */
  function sortCards(cards, key) {
    const read = (c) =>
      key === 'bestseller' ? Number(c.dataset.bestseller || 0) : Number(c.dataset.price || 0);
    return [...cards].sort((a, b) => read(b) - read(a));
  }

  /* Size matcher — a faithful port of the PDP preselectCtlVariants levels
     (js--all-dawn.js), used to align a recommendation's variant with its source
     item's size (cart FBT) and to compute the color-swatch OOS state.
       source: { grouping: string[], title: string }
       variants: [{ id, title, available, size_grouping: string[] }]
     Levels, in the SAME order as the original:
       0 — custom.variant_size_grouping_filter array intersection (merchant-curated)
       1 — exact (normalized) title match
       2 — compound subsize intersection ("2/6m" → ["2m","6m"])
       3 — numeric range: variant titled "lo/hi" (e.g. "6/24m", "4/7") and the
           selected size falls within [lo,hi], same unit (months vs years)
     Two-pass like the original's atcMatch || defaultMatch: try AVAILABLE-only
     variants across all levels first (so an OOS exact size yields the available
     same-group/range size — the "12 OOS → 13" behavior), then all variants.
     NOTE: the original's level-4 fallback (first available) is intentionally
     omitted here — cart-align and swatch-OOS must drop/dim when the requested
     size isn't available, not silently switch to an unrelated size. (M&M popup
     cards still get the full original logic incl. fallback via preselectCtlVariants.)
     Returns the best match, or null when no level matches at all. */
  const norm = (s) => String(s || '').toLowerCase().trim();
  function toSubsizes(str) {
    const parts = norm(str).split('/').map((s) => s.trim()).filter(Boolean);
    const last = parts[parts.length - 1] || '';
    const trailingMonth = parts.length > 1 && /^\d+m$/.test(last) && parts.slice(0, -1).every((p) => /^\d+$/.test(p));
    return trailingMonth ? parts.map((p) => (p.endsWith('m') ? p : p + 'm')) : parts;
  }
  function parseNumericSize(s) {
    if (/^\d+m$/.test(s)) return { value: parseInt(s, 10), unit: 'm' };
    if (/^\d+$/.test(s)) return { value: parseInt(s, 10), unit: 'y' };
    return null;
  }

  /* options.exactOnly — match by exact (normalized) title ONLY, skipping the
     grouping/compound/range levels. Used by the color-swatch OOS state: sibling
     colors of the same style share identical size titles, so exact-title is the
     safest signal (no risk of grouping/range mis-dimming a swatch). */
  function matchVariantBySize(variants, source, options) {
    if (!Array.isArray(variants) || !variants.length) return null;
    const exactOnly = !!(options && options.exactOnly);
    // ignoreAvailability: return the size-matched variant even if it's OOS,
    // instead of preferring an available same-group/range size. The look-bundle
    // unified size selector uses this so picking "12" lands on that product's
    // "12" (shown sold out) rather than silently switching to an available 13.
    const ignoreAvailability = !!(options && options.ignoreAvailability);
    // availableOnly: search ONLY available variants (levels 0-3); return null if
    // none — this is the PDP preselect's "atcMatch". allowFirstAvailableFallback:
    // add level-4 (first available) as a last resort — the PDP "defaultMatch".
    // Together they let the card M&M popup reproduce preselectCtlVariants exactly
    // without touching that PDP method.
    const availableOnly = !!(options && options.availableOnly);
    const allowFallback = !!(options && options.allowFirstAvailableFallback);
    const groupingSet = new Set((source.grouping || []).map((v) => norm(v)));
    const title = norm(source.title);
    const subs = toSubsizes(source.title);
    const parsed = subs.map(parseNumericSize);

    const matchers = [];
    if (!exactOnly && groupingSet.size) {
      matchers.push((v) => Array.isArray(v.size_grouping) && v.size_grouping.length > 0
        && v.size_grouping.some((g) => groupingSet.has(norm(g))));
    }
    matchers.push((v) => norm(v.title) === title);
    if (!exactOnly) {
      matchers.push((v) => { const vs = toSubsizes(v.title); return subs.some((s) => vs.includes(s)); });
      matchers.push((v) => {
        const vp = toSubsizes(v.title);
        if (vp.length !== 2) return false;
        const lo = parseNumericSize(vp[0]);
        const hi = parseNumericSize(vp[1]);
        if (!lo || !hi || lo.unit !== hi.unit) return false;
        return parsed.some((p) => p && p.unit === lo.unit && p.value >= lo.value && p.value <= hi.value);
      });
    }

    if (!ignoreAvailability) {
      const available = variants.filter((v) => v.available);
      for (const m of matchers) { const hit = available.find(m); if (hit) return hit; }
    }
    if (!availableOnly) {
      for (const m of matchers) { const hit = variants.find(m); if (hit) return hit; }
    }
    if (allowFallback) {
      const firstAvailable = variants.find((v) => v.available);
      if (firstAvailable) return firstAvailable;
    }
    return null;
  }

  window.ProductsByHandle = { fetchCardsByHandle, sortCards, matchVariantBySize };
})();
